import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import {
  getImagingTests,
  createImagingTest,
  updateImagingTest,
  deleteImagingTest,
  getImagingModalities,
  getImagingCategories,
  getBodyParts,
  getImagingStatistics
} from '../../../api/imaging';

const ImagingMasterTab = () => {
  const [tests, setTests] = useState([]);
  const [modalities, setModalities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [bodyParts, setBodyParts] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingTest, setEditingTest] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterModality, setFilterModality] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterBodyPart, setFilterBodyPart] = useState('');

  const [formData, setFormData] = useState({
    testName: '',
    modality: '',
    category: '',
    bodyPart: '',
    standardViews: '',
    clinicalIndications: '',
    reportingTemplate: '',
    cost: '',
    specialInstructions: '',
    activeStatus: 'Active'
  });

  useEffect(() => {
    fetchImagingTests();
    fetchDropdownData();
    fetchStatistics();
  }, [currentPage, searchTerm, filterModality, filterCategory, filterBodyPart]);

  const fetchImagingTests = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: 10,
        search: searchTerm || undefined,
        modality: filterModality || undefined,
        category: filterCategory || undefined,
        bodyPart: filterBodyPart || undefined
      };
      
      const response = await getImagingTests(params);
      setTests(response.data?.data?.tests || []);
      setTotalPages(response.data?.data?.pagination?.totalPages || 1);
    } catch (error) {
      console.error('Error fetching imaging tests:', error);
      alert('Failed to fetch imaging tests: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchDropdownData = async () => {
    try {
      const [modalitiesRes, categoriesRes, bodyPartsRes] = await Promise.all([
        getImagingModalities(),
        getImagingCategories(),
        getBodyParts()
      ]);
      
      setModalities(modalitiesRes.data?.data || []);
      setCategories(categoriesRes.data?.data || []);
      setBodyParts(bodyPartsRes.data?.data || []);
    } catch (error) {
      console.error('Error fetching dropdown data:', error);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await getImagingStatistics();
      setStatistics(response.data?.data);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const resetForm = () => {
    setFormData({
      testName: '',
      modality: '',
      category: '',
      bodyPart: '',
      standardViews: '',
      clinicalIndications: '',
      reportingTemplate: '',
      cost: '',
      specialInstructions: '',
      activeStatus: 'Active'
    });
  };

  const handleAddTest = async (e) => {
    e.preventDefault();
    
    if (!formData.testName || !formData.modality || !formData.category || !formData.bodyPart) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      
      const testData = {
        testName: formData.testName,
        modality: formData.modality,
        category: formData.category,
        bodyPart: formData.bodyPart,
        standardViews: formData.standardViews ? formData.standardViews.split(',').map(v => v.trim()) : [],
        clinicalIndications: formData.clinicalIndications ? formData.clinicalIndications.split(',').map(v => v.trim()) : [],
        reportingTemplate: formData.reportingTemplate || undefined,
        cost: formData.cost ? parseFloat(formData.cost) : undefined,
        specialInstructions: formData.specialInstructions || undefined,
        activeStatus: formData.activeStatus || 'Active'
      };

      await createImagingTest(testData);
      alert('Imaging test created successfully');
      setShowAddDialog(false);
      resetForm();
      fetchImagingTests();
      fetchStatistics();
    } catch (error) {
      console.error('Error creating imaging test:', error);
      alert('Failed to create imaging test: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditTest = (test) => {
    setEditingTest(test);
    setFormData({
      testName: test.testName || '',
      modality: test.modality || '',
      category: test.category || '',
      bodyPart: test.bodyPart || '',
      standardViews: Array.isArray(test.standardViews) ? test.standardViews.join(', ') : '',
      clinicalIndications: Array.isArray(test.clinicalIndications) ? test.clinicalIndications.join(', ') : '',
      reportingTemplate: test.reportingTemplate || '',
      cost: test.cost || '',
      specialInstructions: test.specialInstructions || '',
      activeStatus: test.activeStatus || test.status || 'Active'
    });
    setShowEditDialog(true);
  };

  const handleUpdateTest = async (e) => {
    e.preventDefault();
    
    if (!formData.testName || !formData.modality || !formData.category || !formData.bodyPart) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      
      const testData = {
        testName: formData.testName,
        modality: formData.modality,
        category: formData.category,
        bodyPart: formData.bodyPart,
        standardViews: formData.standardViews ? formData.standardViews.split(',').map(v => v.trim()) : [],
        clinicalIndications: formData.clinicalIndications ? formData.clinicalIndications.split(',').map(v => v.trim()) : [],
        reportingTemplate: formData.reportingTemplate || undefined,
        cost: formData.cost ? parseFloat(formData.cost) : undefined,
        specialInstructions: formData.specialInstructions || undefined,
        activeStatus: formData.activeStatus || 'Active'
      };

      await updateImagingTest(editingTest._id, testData);
      alert('Imaging test updated successfully');
      setShowEditDialog(false);
      setEditingTest(null);
      resetForm();
      fetchImagingTests();
      fetchStatistics();
    } catch (error) {
      console.error('Error updating imaging test:', error);
      alert('Failed to update imaging test: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTest = async (id) => {
    if (!window.confirm('Are you sure you want to delete this imaging test?')) {
      return;
    }

    try {
      setLoading(true);
      await deleteImagingTest(id);
      alert('Imaging test deleted successfully');
      fetchImagingTests();
      fetchStatistics();
    } catch (error) {
      console.error('Error deleting imaging test:', error);
      alert('Failed to delete imaging test: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleFilterChange = (filterType, value) => {
    setCurrentPage(1);
    switch (filterType) {
      case 'modality':
        setFilterModality(value);
        break;
      case 'category':
        setFilterCategory(value);
        break;
      case 'bodyPart':
        setFilterBodyPart(value);
        break;
      default:
        break;
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterModality('');
    setFilterCategory('');
    setFilterBodyPart('');
    setCurrentPage(1);
  };

  return (
    <div className="space-y-6">
      {/* Header with Statistics */}
      <div className="bg-gradient-to-r from-[#2D869C]/5 to-[#9DC9D4]/10 p-6 rounded-xl shadow-sm border border-[#2D869C]/20">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-bold text-[#2D869C] flex items-center">
              <Icon name="ScanLine" size={28} className="mr-3" />
              Imaging Master
            </h2>
            <p className="text-gray-600 mt-1">Manage imaging test master data</p>
            {statistics && (
              <div className="mt-2 flex gap-4 text-sm text-gray-600">
                <span>Total Tests: <strong>{statistics.totalTests || 0}</strong></span>
                <span>Active: <strong>{statistics.activeTests || 0}</strong></span>
                <span>Modalities: <strong>{statistics.modalityCount || 0}</strong></span>
              </div>
            )}
          </div>
          <button
            onClick={() => {
              resetForm();
              setShowAddDialog(true);
            }}
            className="inline-flex items-center px-6 py-3 bg-[#2D869C] text-white font-semibold rounded-xl hover:bg-[#2D869C]/90 focus:outline-none focus:ring-2 focus:ring-[#2D869C] focus:ring-offset-2 transition-all duration-200 shadow-md hover:shadow-lg"
          >
            <Icon name="Plus" size={18} className="mr-2" />
            Add Imaging Test
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <input
              type="text"
              placeholder="Search tests..."
              value={searchTerm}
              onChange={handleSearch}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Modality</label>
            <select
              value={filterModality}
              onChange={(e) => handleFilterChange('modality', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Modalities</option>
              {modalities.map((modality) => (
                <option key={modality} value={modality}>{modality}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select
              value={filterCategory}
              onChange={(e) => handleFilterChange('category', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Categories</option>
              {categories.map((category) => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Body Part</label>
            <select
              value={filterBodyPart}
              onChange={(e) => handleFilterChange('bodyPart', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Body Parts</option>
              {bodyParts.map((bodyPart) => (
                <option key={bodyPart} value={bodyPart}>{bodyPart}</option>
              ))}
            </select>
          </div>
        </div>
        {(searchTerm || filterModality || filterCategory || filterBodyPart) && (
          <div className="mt-3">
            <button
              onClick={clearFilters}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Clear all filters
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading && tests.length === 0 ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : tests.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Icon name="Database" size={48} className="mx-auto mb-4 text-gray-400" />
            <p>No imaging tests found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-[#2D869C] to-[#2D869C]/90">
                <tr>
                  <th className="px-6 py-4 text-left text-base font-bold text-white uppercase tracking-wider">
                    Test ID
                  </th>
                  <th className="px-6 py-4 text-left text-base font-bold text-white uppercase tracking-wider">
                    Test Name
                  </th>
                  <th className="px-6 py-4 text-left text-base font-bold text-white uppercase tracking-wider">
                    Modality
                  </th>
                  <th className="px-6 py-4 text-left text-base font-bold text-white uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-4 text-left text-base font-bold text-white uppercase tracking-wider">
                    Body Part
                  </th>
                  <th className="px-6 py-4 text-left text-base font-bold text-white uppercase tracking-wider">
                    Cost (₹)
                  </th>
                  <th className="px-6 py-4 text-left text-base font-bold text-white uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-right text-base font-bold text-white uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {tests.map((test) => (
                  <tr key={test._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-base font-semibold text-gray-900">
                      {test.testId}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-base font-semibold text-gray-900">
                      {test.testName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-base font-semibold text-gray-600">
                      {test.modality}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-base font-semibold text-gray-600">
                      {test.category}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-base font-semibold text-gray-600">
                      {test.bodyPart}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-base font-semibold text-gray-900">
                      {test.cost ? `₹${test.cost.toLocaleString('en-IN')}` : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        test.status === 'Active' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {test.status || 'Active'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-base font-semibold">
                      <div className="flex justify-end space-x-3">
                        <button
                          onClick={() => handleEditTest(test)}
                          className="text-[#CCB478] hover:text-[#CCB478]/80 transition-colors p-1 hover:bg-[#CCB478]/10 rounded-lg"
                          title="Edit"
                        >
                          <Icon name="Edit" size={18} />
                        </button>
                        <button
                          onClick={() => handleDeleteTest(test._id)}
                          className="text-[#E99797] hover:text-[#E99797]/80 transition-colors p-1 hover:bg-[#E99797]/10 rounded-lg"
                          title="Delete"
                        >
                          <Icon name="Trash2" size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}\n              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Page {currentPage} of {totalPages}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add Dialog */}
      {showAddDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
              <h3 className="text-lg font-semibold text-gray-900">Add Imaging Test</h3>
            </div>
            <form onSubmit={handleAddTest} className="px-6 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Test Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="testName"
                    value={formData.testName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., Chest X-Ray PA View"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Modality <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="modality"
                    value={formData.modality}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select Modality</option>
                    {modalities.map((modality) => (
                      <option key={modality} value={modality}>{modality}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select Category</option>
                    {categories.map((category) => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Body Part <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="bodyPart"
                    value={formData.bodyPart}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select Body Part</option>
                    {bodyParts.map((bodyPart) => (
                      <option key={bodyPart} value={bodyPart}>{bodyPart}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cost (₹)
                  </label>
                  <input
                    type="number"
                    name="cost"
                    value={formData.cost}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., 500"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reporting Template
                  </label>
                  <input
                    type="text"
                    name="reportingTemplate"
                    value={formData.reportingTemplate}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., Standard Chest X-Ray Template"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Standard Views (comma separated)
                  </label>
                  <input
                    type="text"
                    name="standardViews"
                    value={formData.standardViews}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., PA View, Lateral View"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Clinical Indications (comma separated)
                  </label>
                  <input
                    type="text"
                    name="clinicalIndications"
                    value={formData.clinicalIndications}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., Cough, Fever, Suspected pneumonia"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Special Instructions
                  </label>
                  <textarea
                    name="specialInstructions"
                    value={formData.specialInstructions}
                    onChange={handleInputChange}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Special instructions for technicians"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    name="activeStatus"
                    value={formData.activeStatus}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Deprecated">Deprecated</option>
                  </select>
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3 border-t border-gray-200 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddDialog(false);
                    resetForm();
                  }}
                  className="px-6 py-2.5 bg-gray-500 text-white font-semibold rounded-xl hover:bg-gray-600 transition-all duration-200 shadow-md"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2.5 bg-[#2D869C] text-white font-semibold rounded-xl hover:bg-[#2D869C]/90 focus:outline-none focus:ring-2 focus:ring-[#2D869C] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-md"
                >
                  {loading ? 'Creating...' : 'Create Test'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Dialog */}
      {showEditDialog && editingTest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
              <h3 className="text-lg font-semibold text-gray-900">Edit Imaging Test</h3>
            </div>
            <form onSubmit={handleUpdateTest} className="px-6 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Test Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="testName"
                    value={formData.testName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Modality <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="modality"
                    value={formData.modality}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select Modality</option>
                    {modalities.map((modality) => (
                      <option key={modality} value={modality}>{modality}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select Category</option>
                    {categories.map((category) => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Body Part <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="bodyPart"
                    value={formData.bodyPart}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select Body Part</option>
                    {bodyParts.map((bodyPart) => (
                      <option key={bodyPart} value={bodyPart}>{bodyPart}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cost (₹)
                  </label>
                  <input
                    type="number"
                    name="cost"
                    value={formData.cost}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reporting Template
                  </label>
                  <input
                    type="text"
                    name="reportingTemplate"
                    value={formData.reportingTemplate}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., Standard Chest X-Ray Template"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Standard Views (comma separated)
                  </label>
                  <input
                    type="text"
                    name="standardViews"
                    value={formData.standardViews}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Clinical Indications (comma separated)
                  </label>
                  <input
                    type="text"
                    name="clinicalIndications"
                    value={formData.clinicalIndications}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Special Instructions
                  </label>
                  <textarea
                    name="specialInstructions"
                    value={formData.specialInstructions}
                    onChange={handleInputChange}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Special instructions for technicians"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    name="activeStatus"
                    value={formData.activeStatus}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Deprecated">Deprecated</option>
                  </select>
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3 border-t border-gray-200 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditDialog(false);
                    setEditingTest(null);
                    resetForm();
                  }}
                  className="px-6 py-2.5 bg-gray-500 text-white font-semibold rounded-xl hover:bg-gray-600 transition-all duration-200 shadow-md"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2.5 bg-[#2D869C] text-white font-semibold rounded-xl hover:bg-[#2D869C]/90 focus:outline-none focus:ring-2 focus:ring-[#2D869C] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-md"
                >
                  {loading ? 'Updating...' : 'Update Test'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImagingMasterTab;
